import { Box, Checkbox, Stack, Typography } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-pro'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import {
  invalidateAppFeatureCache,
  useGetFeatureCustomerAssignments,
  useUpdateFeatureCustomerAssignments,
} from 'src/api/app-features'
import { FeatureCustomerAssignmentRow, UpdateFeatureCustomerAssignmentItem } from 'src/generated-types/app-feature'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import GenericTable from 'src/components/table/GenericTable'
import { ShowDialogProps } from 'src/show-dialog'
import { invalidateCustomer } from 'src/api/customer'
import { MainButton } from '@lovoldsystem/ui'

type RowState = FeatureCustomerAssignmentRow & { isChecked: boolean }

type AddCustomersToFeatureDialogProps = ShowDialogProps & {
  featureId: number
  featureName: string
}

export const AddCustomersToFeatureDialog: FC<AddCustomersToFeatureDialogProps> = ({ pop, featureId, featureName }) => {
  const { t } = useTranslation(['feature-flags', 'common', 'admin-customer'])
  const { data, isLoading, isFetching, isError, error } = useGetFeatureCustomerAssignments(featureId)
  const { mutateAsync, isLoading: isSaving } = useUpdateFeatureCustomerAssignments(featureId)
  const [rows, setRows] = useState<RowState[]>([])

  const normalizeRow = useCallback(
    (row: Record<string, unknown>): FeatureCustomerAssignmentRow => ({
      customerId: Number(row.customerId ?? row.CustomerId ?? 0),
      name: String(row.name ?? row.Name ?? ''),
      organizationNr: String(row.organizationNr ?? row.OrganizationNr ?? ''),
      mappingId: Number(row.mappingId ?? row.MappingId ?? 0),
      isAssigned: Boolean(row.isAssigned ?? row.IsAssigned ?? false),
    }),
    [],
  )

  useEffect(() => {
    if (isError) {
      const message =
        (error as AxiosError<{ message?: string }>)?.response?.data?.message ?? t('feature-customers-load-failed')
      toast.error(message)
      setRows([])
      return
    }

    if (data && data.length > 0) {
      setRows(
        data.map(raw => {
          const row = normalizeRow(raw as Record<string, unknown>)
          return {
            ...row,
            isChecked: row.isAssigned,
          }
        }),
      )
    } else if (data && data.length === 0 && !isLoading && !isFetching) {
      setRows([])
    }
  }, [data, error, isError, isFetching, isLoading, normalizeRow, t])

  const onCheckBoxSelect = useCallback((customerId: number, checked: boolean) => {
    setRows(prev => prev.map(row => (row.customerId === customerId ? { ...row, isChecked: checked } : row)))
  }, [])

  const save = async () => {
    const changes: UpdateFeatureCustomerAssignmentItem[] = []
    rows.forEach(row => {
      if (row.mappingId === 0 && row.isChecked) {
        changes.push({ customerId: row.customerId, isAssigned: true })
      } else if (row.mappingId > 0 && !row.isChecked) {
        changes.push({ customerId: row.customerId, isAssigned: false })
      } else if (row.mappingId > 0 && row.isChecked && !row.isAssigned) {
        changes.push({ customerId: row.customerId, isAssigned: true })
      }
    })

    if (changes.length === 0) {
      pop()
      return
    }

    try {
      await mutateAsync(changes, {
        onSuccess: () => {
          invalidateAppFeatureCache.getFeatureCustomerAssignments(featureId)
          invalidateAppFeatureCache.getAdminAppFeatures()
          invalidateAppFeatureCache.getAppFeatureMap()
          invalidateCustomer.getCustomersNameWithProjectPlanningAccess()
        },
      })
      toast.success(t('feature-customers-saved'))
      pop(true)
    } catch {
      toast.error(t('feature-customers-save-failed'))
    }
  }

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'customerId', headerName: 'ID', width: 90 },
      { field: 'name', headerName: String(t('name', { ns: 'common' })), flex: 1, minWidth: 180 },
      {
        field: 'organizationNr',
        headerName: 'Organization number',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'isChecked',
        headerName: t('col-assigned'),
        width: 100,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Checkbox
            checked={row.isChecked}
            disabled={isSaving}
            onChange={e => onCheckBoxSelect(row.customerId, e.target.checked)}
            size="small"
          />
        ),
      },
    ],
    [isSaving, onCheckBoxSelect, t],
  )

  return (
    <GenericDialogLayout
      title={t('add-customers-dialog-title', { name: featureName })}
      pop={() => pop()}
      removedCloseButton={false}
      sx={{ pb: 0, minWidth: 720 }}
      actions={[
        <MainButton
          key="save-feature-customers"
          variant="contained"
          loading={isSaving}
          disabled={isSaving || isLoading}
          onClick={() => void save()}
        >
          {t('save', { ns: 'common' })}
        </MainButton>,
      ]}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('add-customers-help')}
        </Typography>
        <Box>
          <GenericTable
            id={`feature-customers-${featureId}`}
            enableToolbar
            enablePagination
            enableQuickFilter
            data={rows}
            columns={columns}
            loading={isLoading || isFetching}
            getRowId={row => row.customerId}
            getRowHeight={() => 48}
            autoHeight
            pageSize={10}
            pageOptions={[10, 25, 50]}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            noRowsMessage={isError ? t('feature-customers-load-failed') : undefined}
          />
        </Box>
      </Stack>
    </GenericDialogLayout>
  )
}
